import { createContext, useContext, type ReactNode } from "react";
import type {
  PortalReviewScenarios,
  PortalSignInAdapter,
} from "./customer-portal-access-types";

const PortalSignInAdapterContext = createContext<PortalSignInAdapter | null>(
  null,
);

/**
 * Makes the sign-in adapter available to the customer portal's landing gate and its welcome,
 * customer chooser, no-access and blocked pages. Every portal build mounts one: the real adapter
 * (`createPortalSignInAdapter`) in the real build, the mock twin in the mock build.
 */
export function PortalSignInAdapterProvider({
  adapter,
  children,
}: {
  adapter: PortalSignInAdapter;
  children: ReactNode;
}) {
  return (
    <PortalSignInAdapterContext.Provider value={adapter}>
      {children}
    </PortalSignInAdapterContext.Provider>
  );
}

export function usePortalSignInAdapter(): PortalSignInAdapter {
  const adapter = useContext(PortalSignInAdapterContext);
  if (!adapter) {
    throw new Error(
      "usePortalSignInAdapter must be used inside PortalSignInAdapterProvider",
    );
  }
  return adapter;
}

const PortalReviewScenariosContext =
  createContext<PortalReviewScenarios | null>(null);

/** Mounted by the mock build only: the review tool's sign-in states. */
export function PortalReviewScenariosProvider({
  scenarios,
  children,
}: {
  scenarios: PortalReviewScenarios;
  children: ReactNode;
}) {
  return (
    <PortalReviewScenariosContext.Provider value={scenarios}>
      {children}
    </PortalReviewScenariosContext.Provider>
  );
}

/** The review scenarios, or null in the real build, which has none. */
export function usePortalReviewScenarios(): PortalReviewScenarios | null {
  return useContext(PortalReviewScenariosContext);
}
